import { deriveIntentForPlanningReport } from './generationImpact/selectedAssessmentIntent';

/**
 * reportVerdict — sentiment-first verdict helper (SF pack, 2026-05-13).
 *
 * Replaces user-visible numeric `overallScore` (0-100) with a Green / Amber /
 * Red tier plus a type-specific qualitative label ("Strong", "Conditional",
 * "Challenging", etc.). The internal `report.overallScore` is still computed
 * by the deterministic scorer (used for ranking/sort within a tier and for
 * admin power-user disclosure), but the display layer routes through
 * `getDisplayVerdict()` so users never see the raw number.
 *
 * Architecture:
 *
 *   deterministic scorer (siteRagService.scoreSite*)
 *        ↓ writes report.overallScore (0-100, never displayed)
 *   tierFromScore(score, reportType)
 *        ↓ banded into green / amber / red (test-fit → 'none')
 *   reconcileVerdict(baseTier, report.verdictLean?)
 *        ↓ LLM lean may shift ±1 tier with cited evidence (guardrail:
 *          green can never become red and vice versa; low-quality
 *          drivers cause the lean to be dropped)
 *   final ReportVerdict { tier, label, source, internalScore?, lean? }
 *
 * The LLM verdictLean signal is captured from every narrative response schema
 * (see geminiService.ts), persisted on `report.verdictLean`, and consumed here
 * to produce the final user-facing tier. When the LLM doesn't emit a lean
 * (or emits `tierAdjustment: 0`), the deterministic tier wins.
 *
 * Test-fit is the no-scoring precedent: `usesScoring: false` means
 * `report.overallScore` is the constant `: 50` literal that siteRagService
 * stamps for test-fit; we short-circuit on `reportType === 'test-fit'` and
 * return `tier: 'none'` so the cover/body render qualitative copy without a
 * misleading tier.
 */
import type { ProjectIntent, SiteRagReport } from '../types/siteRag';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../config/reportTypes';
import { reportScoreBasis, resolveSentimentIndexScore } from './reportScoringPolicy';
import { formatDroughtExposedSources, hasDroughtExposedSource } from './intent/waterSource';
import { intentHasExplicitGasFreePowerMix } from './projectIntent';
import { waterScreenHasDeniedPermit } from './portfolio/waterScreenEvidence';
import {
  summarizeIntentFeasibility,
  type PhaseFeasibilityVerdict,
} from './phaseFeasibility';

// ── Types ────────────────────────────────────────────────────────────────────

export type VerdictTier = 'green' | 'amber' | 'red' | 'none';
export type VerdictSource = 'deterministic' | 'llm-leaned' | 'none';

/**
 * LLM-emitted verdict lean. Captured from narrative response schemas in
 * geminiService.ts and persisted on `report.verdictLean`. `tierAdjustment` is
 * one of -1, 0, +1; `drivers` are 1-4 short bullet strings citing narrative
 * evidence justifying the adjustment (required when adjustment != 0).
 */
export interface VerdictLean {
  tierAdjustment: -1 | 0 | 1;
  drivers?: string[];
}

export interface ReportVerdict {
  /** Final user-facing tier after LLM lean reconciliation. 'none' for test-fit
   *  (no deterministic scorer) and for reports with no trustworthy score. */
  tier: VerdictTier;
  /** Type-specific qualitative label ("Strong" / "Conditional" /
   *  "Challenging" for feasibility; "Low impact" / "Mitigable" / "High
   *  impact" for environmental; "—" when tier === 'none'). */
  label: string;
  /** Report type's shortLabel from REPORT_TYPE_REGISTRY ("Feasibility",
   *  "Environmental", …). Convenience for chip rendering. */
  reportTypeShortLabel: string;
  /** Where the tier came from — 'deterministic' (banded score only),
   *  'llm-leaned' (LLM signal shifted the tier), or 'none' (no score). */
  source: VerdictSource;
  /** A frozen saved decision constrained a more optimistic computed display. */
  savedDecisionPending?: boolean;
  savedDecisionConflict?: string;
  savedDecisionCap?: { preCapTier: VerdictTier; recordedTier: VerdictTier };
  /** The deterministic 0-100 score backing this verdict. NEVER displayed
   *  except in admin power-user disclosure. Used for in-tier ranking. */
  internalScore?: number;
  /** Deterministic tier BEFORE LLM reconciliation. Surfaced to consumers
   *  rendering the "Why {tier}?" expandable so they can show
   *  "deterministic data alone scores this {baseLabel}; narrative
   *  adjusts {direction} one tier." */
  deterministicTier?: VerdictTier;
  /** Absorbed water-screen refusal, retained separately from the numeric score. */
  waterScreenCap?: { preCapTier: VerdictTier; note: string };
  /** LLM lean object — present iff `source === 'llm-leaned'` and the
   *  lean was applied (driver-quality guardrail passed). */
  lean?: { delta: -1 | 0 | 1; drivers: string[] };
  /**
   * R-FIN-VERDICT-ELCC — firm-capacity (ELCC) cap audit note. Present ONLY
   * when the project intent's worst-case per-phase firm-capacity feasibility
   * (`summarizeIntentFeasibility`) FORCED the displayed tier DOWN from where
   * the score/lean would otherwise have landed:
   *
   *   - worstVerdict 'infeasible' → caps the tier at 'red' (a behind-the-meter
   *     load that the declared AND-combined power mix cannot firm cannot be
   *     shown Strong/Go or even Conditional).
   *   - worstVerdict 'marginal'   → caps the tier at 'amber' (Conditional).
   *
   * `null`/`'feasible'` worst-verdicts (the COMMON case — OR-mode, single
   * source, or unconfigured phases) apply NO cap and leave this undefined,
   * so non-AND-mix sites are never regressed. The note carries the
   * pre-cap tier so consumers can render "why was this capped?".
   */
  firmCapacityCap?: {
    /** The worst per-phase firm-capacity verdict that triggered the cap. */
    worstVerdict: Extract<PhaseFeasibilityVerdict, 'marginal' | 'infeasible'>;
    /** Tier the score + lean would have shown before the cap was applied. */
    preCapTier: VerdictTier;
    /** One-line audit rationale for the cap. */
    note: string;
    /**
     * P3-3 — true when the cap was surfaced for a NO-SCORER report type
     * (executive-summary / sentiment, tier 'none'). The banded `tier` is left
     * 'none' (no fabricated band), but the cap is still recorded so the
     * exec-summary `overallVerdict` reconciliation can downgrade the headline
     * GO/NO-GO (a firm-capacity shortfall must not surface as PROCEED even when
     * there is no deterministic scorer to band).
     */
    forNoScorerType?: boolean;
  };
  /**
   * EA8 — evidence-tier cap audit note. Present ONLY when the report carried a
   * GREEN/Strong banded tier that the evidence profile (mostly Tier-3 regional
   * baselines / Tier-4 LLM synthesis, below `STRONG_VERDICT_TIER1_FLOOR`) could
   * not defend, so the displayed `tier` was lowered to 'amber'.
   *
   * Before this binding the EA8 cap lowered NOTHING — it only appended a caption
   * in `ConclusionCallout`; the verdict dot, journal covers, data-rag tone, and
   * portfolio band still rendered GREEN on Tier-3/4 evidence. Now the cap lowers
   * `tier` so EVERY surface inherits it; the ConclusionCallout annotation becomes
   * the explanation rather than the only effect.
   *
   * Signal-gated: NO cap (undefined) unless `sectionProvenance` is recorded AND
   * the tier1 share is below the floor — a legacy report with no provenance is
   * byte-stable (the no-override invariant).
   */
  evidenceTierCap?: {
    /** Tier the score + lean + firm-capacity cap would have shown before EA8. */
    preCapTier: VerdictTier;
    /** Weighted Tier-1 evidence share that fell below the strong-verdict floor. */
    tier1Share: number;
    /** Count of critical uploads that would lift the cap (annotation guidance). */
    upliftDocCount: number;
    /** One-line audit rationale for the cap. */
    note: string;
  };
  /**
   * HR-4 — multi-axis binding-constraint cap. The headline verdict was bound to
   * a hard, declared development constraint OTHER than firm-capacity (ELCC):
   *
   *   - WATER rights: an unpermitted surface / groundwater source (drought
   *     curtailment exposure for cooling withdrawal).
   *   - FUEL supply: no declared gas supply, or a required pipeline lateral
   *     (long-lead, single-path COD dependency for a gas build).
   *   - INTERCONNECTION queue: an early-queue grid-import-dependent request
   *     (pre-application / queued / study — multi-year energization-date risk).
   *
   * Each of these is a CONDITION, not an infeasibility, so the worst they cap at
   * is 'amber' (Conditional) — they can never force 'red' on their own (that is
   * reserved for an INFEASIBLE firm-capacity verdict). The cap NEVER raises a
   * tier; absent any of these signals it is undefined (byte-stable — the common
   * case). It STACKS beneath the firm-capacity (ELCC) cap inside
   * `capTierByFirmCapacity`: the displayed tier is the lowest ceiling any cap
   * imposes, and the constraint cap is only surfaced when it is the binding one.
   */
  constraintCap?: {
    /** The binding constraint(s) that triggered the cap (1+). */
    constraints: BindingConstraint[];
    /** Tier the score + lean + firm-capacity cap would have shown before this cap. */
    preCapTier: VerdictTier;
    /** One-line audit rationale for the cap. */
    note: string;
    /**
     * Parity with `firmCapacityCap.forNoScorerType` — true when the cap was
     * surfaced for a NO-SCORER report type (executive-summary / sentiment, tier
     * 'none'). The banded `tier` stays 'none' (no fabricated band) but the cap
     * is still recorded so the exec-summary headline GO/NO-GO can be downgraded.
     */
    forNoScorerType?: boolean;
  };
  /**
   * Q12 / D6 — the deterministic FINANCIAL verdict was WITHHELD (the engine
   * stamp carries `band: null`). Present ONLY for a financial report that has
   * a stamp whose band is null; every other report/tier leaves it undefined.
   *
   * A withheld verdict is NOT a tier-'none' "no score yet": the engine ran and
   * declined to band the result. Consumers must render it as WITHHELD (and say
   * WHICH kind — "inputs incoherent" reads very differently from "inputs
   * missing") rather than falling through to the generic em-dash, and must
   * never promote it to a Conditional.
   */
  withheldVerdict?: {
    /** Why the band is null, when the stamp recorded a reason. */
    reason?: FinancialWithheldReason;
    /** Short display label ("Verdict withheld"). Mirrored into `label`. */
    label: string;
    /** One-line reader-facing explanation of the withholding. */
    note: string;
  };
  /** Derived adequacy gate band. */
  band?: 'green' | 'amber' | 'red' | null;
  /** Derived adequacy gate label. */
  bandLabel?: string;
}

/**
 * HR-4 — a single declared binding development constraint (non-firm-capacity)
 * that caps the headline verdict at Conditional. Each carries its own audit
 * note so the "why was this capped?" surface can list every binding constraint.
 */
export interface BindingConstraint {
  kind: 'water-rights' | 'fuel-supply' | 'interconnection-queue';
  /** Human-readable one-line reason. */
  note: string;
}

// ── Verdict-lean policy (decision Q12) ───────────────────────────────────────

/**
 * PER-REPORT-TYPE VERDICT-LEAN POLICY.
 *
 * `verdictLean` is an LLM narrative signal ("adjust the deterministic verdict
 * tier by at most ±1", captured from the narrative response schema and
 * persisted TYPE-AGNOSTICALLY on `report.verdictLean`). Applying it is
 * defensible when the deterministic tier is a WEIGHTED MEAN OF QUALITATIVE
 * CATEGORY SCORES: the narrative can legitimately carry evidence the category
 * rubric never encoded, so a one-tier nudge adds information.
 *
 * It is NOT defensible when the tier is a MODEL OUTPUT. `financial` is banded
 * by `scoreSiteFinancial` directly off the deterministic project-finance
 * engine — NPV, IRR vs hurdle, payback vs life, P(NPV>0). Those numbers already
 * ARE the evidence; the narrative is a description OF them, generated FROM
 * them. Letting it move the band is not new information — it is noise with a
 * confidence interval, and it is directionally dangerous in both directions: an
 * Unfinanceable model (NPV < 0) can be leaned UP to Conditional, and a Viable
 * one leaned DOWN, with no arithmetic behind either move. Worse, the financial
 * type reuses `EXECUTIVE_SUMMARY_SCHEMA`, so it carries a `verdictLean` it never
 * asked for and which the stamp never strips.
 *
 * So the exemption is expressed HERE, as a narrow, explicit, per-type policy
 * table rather than an `if (reportType === 'financial')` scattered through
 * `getDisplayVerdict`: the next deterministic-verdict type (an engine-banded
 * interconnection cost model, say) is exempted by adding ONE row, and the
 * reason for the row lives beside it.
 *
 * NOTE: this exempts the LEAN only. The evidence-tier (EA8), firm-capacity
 * (ELCC) and binding-constraint caps still apply to financial reports — they
 * are monotonic-DOWN and encode real epistemic limits, not narrative opinion.
 * See `resolveFinancialDisplayVerdict` for the disclosure contract that keeps
 * them honest.
 */
export const VERDICT_LEAN_EXEMPT_REPORT_TYPES: readonly ReportTypeId[] = [
  // Banded from the project-finance engine, not from category prose.
  'financial',
  // P1.2 / P1.6 (2026-09-05) — Water Resource is in ALWAYS_REQUIRED_FOR_GO and
  // keeps its 'hard' floor policy, so its red verdict DECLINES a site (PATH 2
  // in the rollup, `computeSourceFloor` in the composite). `reconcileVerdict`
  // lets a lean move the tier one step on a single ≥8-character driver, so an
  // amber earned by the category composite could be pushed to red by one
  // narrative bullet — narrative holding the strongest verdict in the system,
  // which is exactly what the cap ladder's doctrine forbids. A verdict with
  // decline authority must be deterministically earned; the lean stays in the
  // narrative where it was written. Same treatment `financial` has.
  // Phase 3 part B (2026-09-07): the type is RETIRED; the exemption stays so
  // an existing water report's verdict is read the way it was earned.
  'water-resource',
  // D1 (two-stage, 2026-09-06) — environmental is always required; its floor
  // is mitigable, and a narrative lean must not be what earns the cap.
  'environmental',
] as const;

/** True when an LLM `verdictLean` may move this report type's tier at all. */
export function reportTypeAllowsVerdictLean(
  reportType: ReportTypeId | undefined,
): boolean {
  if (!reportType) return true;
  return !VERDICT_LEAN_EXEMPT_REPORT_TYPES.includes(reportType);
}

// ── Withheld financial verdict (decision Q12, item 3) ────────────────────────

/** Why `financialAnalysis.band` is null. Mirrors the stamp's own union. */
export type FinancialWithheldReason = 'inputs-insufficient' | 'inputs-incoherent' | 'scope-limited';

/** Short display label for a withheld deterministic verdict — the one name
 *  every surface uses (P2.3); defined on the leaf `decisionBands` module. */
import { VERDICT_WITHHELD_LABEL } from './portfolio/decisionBands';
export { VERDICT_WITHHELD_LABEL };

/** Two-word name for WHY the band was withheld (for compact chips/cells). */
export function financialWithheldReasonLabel(
  reason: FinancialWithheldReason | undefined,
): string {
  if (reason === 'scope-limited') return 'scope limited';
  if (reason === 'inputs-incoherent') return 'inputs incoherent';
  if (reason === 'inputs-insufficient') return 'inputs insufficient';
  return 'not gradeable';
}

/**
 * One-line reader-facing explanation of a withheld financial verdict.
 *
 * The two reasons are NOT interchangeable and must not collapse into one
 * sentence: `inputs-insufficient` means the engine could not compute at all
 * (fix = supply the inputs); `inputs-incoherent` means it computed and the
 * result is not trustworthy (fix = reconcile the bases). Telling a user to
 * "add data" when the real problem is a $137/kW CAPEX basis sends them the
 * wrong way.
 */
export function financialWithheldNote(
  reason: FinancialWithheldReason | undefined,
): string {
  if (reason === 'scope-limited') return 'Verdict withheld — fictional inputs or unpriced scope limit decision use. Calculated cash flows are screening arithmetic, not an investment recommendation or commitment.';
  if (reason === 'inputs-incoherent') {
    return 'Verdict withheld — inputs incoherent: the engine can compute, but the CAPEX basis and the revenue basis describe different plant (or an output breached a plausibility ceiling), so banding them would mislead. Reconcile the bases and re-run.';
  }
  if (reason === 'inputs-insufficient') {
    return 'Verdict withheld — inputs insufficient: the project-finance engine could not produce an NPV from the declared assumptions. Complete the financial model and re-run.';
  }
  return 'Verdict withheld — the deterministic engine did not band these inputs.';
}

/**
 * The deterministic tier for a FINANCIAL report, read from the ENGINE STAMP
 * rather than from `overallScore`.
 *
 * `applyFinancialAnalysisResultToReport` writes both (`overallScore` is banded
 * so `tierFromScore` reproduces `band` exactly), so in production these agree —
 * but they are two copies of one fact, and the copy that IS the verdict is the
 * band. Reading the band directly means a body section and a cover can never
 * disagree because one of them re-banded a score, and it makes the withheld
 * case explicit instead of inferring it from a `0` sentinel.
 *
 * Returns `null` (⇒ fall back to score banding, byte-stable) for every
 * non-financial report and for a legacy financial report with no stamp.
 */
function financialStampTier(report: SiteRagReport): VerdictTier | null {
  if (report.reportType !== 'financial') return null;
  const band = report.financialAnalysis?.band;
  if (band === undefined) return null; // no stamp (or legacy) — score banding.
  if (band === 'green' || band === 'amber' || band === 'red') return band;
  return 'none'; // band === null ⇒ WITHHELD (never a fabricated Conditional).
}

// ── Tier label maps ──────────────────────────────────────────────────────────
//
// Per-report-type qualitative labels for the overall verdict tier. Drawn from
// SF pack §0 table — vocabulary chosen to read sentiment-first without
// numeric pseudo-precision.

const OVERALL_LABELS: Record<ReportTypeId, Record<VerdictTier, string>> = {
  feasibility: {
    green: 'Strong',
    amber: 'Conditional',
    red: 'Challenging',
    none: '—',
  },
  environmental: {
    green: 'Low impact',
    amber: 'Mitigable',
    red: 'High impact',
    none: '—',
  },
  'power-availability': {
    green: 'Plentiful',
    amber: 'Constrained',
    red: 'Scarce',
    none: '—',
  },
  'water-resource': {
    green: 'Abundant',
    amber: 'Constrained',
    red: 'Scarce',
    none: '—',
  },
  interconnection: {
    green: 'Favorable',
    amber: 'Workable',
    red: 'Difficult',
    none: '—',
  },
  'supply-chain-risk': {
    green: 'Bankable',
    amber: 'Manageable',
    red: 'Exposed',
    none: '—',
  },
  'test-fit': {
    // Test-fit has no deterministic scorer (usesScoring: false), so it
    // resolves to 'none' by default; the labels below are reserved for
    // a hypothetical future test-fit scorer (relax `tierFromScore` then).
    green: 'Spacious fit',
    amber: 'Tight fit',
    red: 'Insufficient',
    none: '—',
  },
  // Public Sentiment Report. The GENERIC category scorer is skipped
  // (`ReportPromptConfig.usesScoring: false`), so the authoritative score is
  // the PSR-2 SENTIMENT INDEX 0-100 — resolved via the 'sentiment-index' score
  // basis and banded on the uniform 67/34 thresholds:
  //   green  → Supportive
  //   amber  → Mixed
  //   red    → Opposed
  //   none   → '—'  (index absent on a PSR-1 scaffold — no fabricated verdict)
  // As a MITIGABLE decision source this tier caps the Go/No-Go rollup at
  // CONDITIONAL and can never floor it to NO-GO (see MITIGABLE_FLOOR_SOURCES).
  'public-sentiment': {
    green: 'Supportive',
    amber: 'Mixed',
    red: 'Opposed',
    none: '—',
  },
  'custom-brief': {
    green: 'Clear',
    amber: 'Qualified',
    red: 'Material gaps',
    none: '—',
  },
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. usesScoring: true;
  // `scoreSiteFinancial` bands the deterministic project-finance engine
  // outputs (NPV / IRR-vs-hurdle / payback / P(positive NPV)) into a tier:
  //   green  → Viable        (NPV>0 AND IRR≥hurdle AND payback≤life AND pPos≥0.5)
  //   amber  → Conditional   (NPV≥0 with one lever marginal)
  //   red    → Unfinanceable (NPV<0 OR IRR materially below hurdle OR pPos<0.3)
  //   none   → '—'           (inputs insufficient — no fabricated downgrade)
  financial: {
    green: 'Viable',
    amber: 'Conditional',
    red: 'Unfinanceable',
    none: '—',
  },
  'executive-summary': {
    green: 'Viable',
    amber: 'Conditional',
    red: 'No-Go', // R-130-4
    none: '—',
  },
};

/**
 * Per-category qualitative labels — shorter than the overall labels, used
 * inside the per-category table (where the report-type context is already
 * established by the surrounding section heading). Kept type-agnostic for
 * now; report-type-specific category vocab could split this map further
 * if a particular type wants different wording.
 */
const CATEGORY_LABELS: Record<VerdictTier, string> = {
  green: 'Strong',
  amber: 'Watch',
  red: 'Risk',
  none: '—',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Band a deterministic 0-100 score into a tier for a given report type.
 *
 * Uniform feasibility-style bands (0-33 red, 34-66 amber, 67-100 green) apply
 * to every report type with an authoritative score. Types whose score basis is
 * 'none' (test-fit, custom-brief, executive-summary) always return 'none' —
 * the Site Rag scorer stamps a `: 50` literal on them and banding that would
 * show e.g. "Tight fit" with no scoring basis. The deterministic scorers
 * themselves use the same thresholds when they compute `report.overallStatus`,
 * so banding is consistent across the surface.
 */
/**
 * The SINGLE banding threshold pair for a 0-100 report score.
 *
 * These were duplicated as 67/34 here and 70/40 in
 * `SiteRagService.calculateStatus` (which writes `report.overallStatus` and every
 * category `status`), so a score of 68 read AMBER in the report body and GREEN
 * in its verdict — and `decisionAnalysis.fromReport` banded off the amber one
 * while the Go/No-Go rollup banded off the green one. Both now import from here.
 */
export const VERDICT_GREEN_THRESHOLD = 67;
export const VERDICT_AMBER_THRESHOLD = 34;

export function tierFromScore(
  score: number | null | undefined,
  reportType: ReportTypeId | undefined,
): VerdictTier {
  const basis = reportScoreBasis(reportType);
  if (basis === 'none') return 'none';

  if (score === null || score === undefined) return 'none';
  if (!Number.isFinite(score)) return 'none';
  // 0 is the "uninitialised" sentinel that siteRagService.scoreSite* never
  // legitimately emits — treat as 'none' to match getDisplayScore's behavior.
  // The SENTIMENT INDEX is exempt: 0 there is a legitimate "maximally opposed"
  // reading, not an uninitialised value, and must band red rather than vanish.
  if (score <= 0 && basis !== 'sentiment-index') return 'none';

  if (score >= VERDICT_GREEN_THRESHOLD) return 'green';
  if (score >= VERDICT_AMBER_THRESHOLD) return 'amber';
  return 'red';
}

/**
 * Resolve the qualitative label for a tier in a given report type, with an
 * optional 'category' context for shorter per-category vocab.
 */
export function tierLabel(
  tier: VerdictTier,
  reportType: ReportTypeId | undefined,
  context: 'overall' | 'category' = 'overall',
): string {
  if (context === 'category') return CATEGORY_LABELS[tier];
  const id = (reportType ?? 'feasibility') as ReportTypeId;
  const labels = OVERALL_LABELS[id] ?? OVERALL_LABELS.feasibility;
  return labels[tier];
}

/**
 * Reconcile a deterministic tier with an LLM lean. The lean can adjust the
 * tier by ±1 with cited evidence; safety bounds:
 *
 *   - green ↔ red is NEVER permitted (a ±2 lean clamps to the nearest tier).
 *   - missing or low-quality drivers (each < 8 chars after trim, or fewer
 *     than 1 driver) cause the lean to be dropped entirely.
 *   - a deterministic 'none' tier is preserved (no LLM signal can elevate a
 *     no-score report into a banded tier).
 */
export function reconcileVerdict(
  deterministicTier: VerdictTier,
  lean: VerdictLean | undefined,
): VerdictTier {
  if (!lean || lean.tierAdjustment === 0) return deterministicTier;
  if (deterministicTier === 'none') return 'none';

  // Defense-in-depth: drop low-quality leans before applying.
  const drivers = (lean.drivers ?? []).map(d => d?.trim?.() ?? '').filter(d => d.length >= 8);
  if (drivers.length === 0) return deterministicTier;

  // Adjustment must be an integer in [-1, +1]. Anything else is the LLM
  // ignoring the schema enum; drop the lean rather than amplify the drift.
  const delta = lean.tierAdjustment;
  if (delta !== -1 && delta !== 1) return deterministicTier;

  // Apply with clamp + the green↔red guardrail.
  const order: VerdictTier[] = ['red', 'amber', 'green'];
  const idx = order.indexOf(deterministicTier);
  if (idx < 0) return deterministicTier;
  const targetIdx = Math.max(0, Math.min(2, idx + delta));
  return order[targetIdx];
}

/**
 * Resolve the score that backs the verdict — internal-only. This routes
 * through the same precedence as the legacy `getDisplayScore` (deterministic
 * wins; legacy LLM accepted only when on a 0-100 scale; test-fit always
 * null) but the value is consumed for tier banding and sort order, NOT
 * displayed to the user.
 */
function resolveInternalScore(report: SiteRagReport): number | null {
  const basis = reportScoreBasis(report.reportType);
  if (basis === 'none') return null;

  // Public Sentiment: the authoritative score is the PSR-2 sentiment index.
  // `report.overallScore` on a PSR is the `: 50` placeholder siteRagService
  // falls back to when `categories` is undefined — reading it would band every
  // PSR "Mixed" regardless of findings. Absent index ⇒ null ⇒ tier 'none'.
  if (basis === 'sentiment-index') return resolveSentimentIndexScore(report);

  const det = report.overallScore;
  if (typeof det === 'number' && Number.isFinite(det) && det > 0) {
    return Math.max(0, Math.min(100, det));
  }

  const legacy = report.conclusion?.overallScore;
  if (typeof legacy === 'number' && Number.isFinite(legacy)) {
    // Reject the 0-10 bad-scale artifact (matches getDisplayScore).
    if (legacy >= 0 && legacy <= 10) return null;
    return Math.max(0, Math.min(100, legacy));
  }

  return null;
}

/* ── R-FIN-VERDICT-ELCC — firm-capacity (ELCC) verdict cap ──────────────
 *
 * The headline verdict must be bound to firm-capacity reality: if the
 * project intent declares an AND-combined power mix that physically cannot
 * firm the 24/7 load (per `summarizeIntentFeasibility`, which grades on
 * ELCC + planning reserve — NOT capacity factor), the displayed verdict
 * cannot credibly read Strong/Go. We CAP the tier:
 *
 *   worstVerdict 'infeasible' → cap at 'red'   (cannot be green/amber)
 *   worstVerdict 'marginal'   → cap at 'amber' (cannot be green)
 *
 * The cap NEVER raises a tier — it only forces it down. A null worst-verdict
 * (OR-mode / single source / unconfigured phases — the common case) applies
 * NO cap so non-AND-mix sites are never regressed. This intentionally
 * stacks with the EA8 evidence-tier cap and the SF lean: each can only
 * push the verdict toward caution.
 */

/** Tier-ordering for the cap (worst → best). */
const CAP_TIER_ORDER: readonly VerdictTier[] = ['red', 'amber', 'green'];

/* ── HR-4 — multi-axis binding-constraint cap (folded into the firm-capacity cap) ──
 *
 * Beyond firm-capacity (ELCC), the headline verdict must be bound to the other
 * HARD, declared development constraints that a Strong/Go cannot credibly ignore:
 * unpermitted water rights, a fuel-supply gap, and an early interconnection-queue
 * position. These are the SAME signals the deterministic per-type scorers already
 * penalise (`scoreSiteWater` / `scoreSiteSupplyChain` / `scoreSitePower`), lifted
 * to a verdict-side cap so they bind the HEADLINE, not just a category score.
 *
 * Severity model: each is a CONDITION (mitigable), never an infeasibility, so the
 * worst any of them caps at is 'amber' (Conditional). Only an INFEASIBLE
 * firm-capacity verdict caps at 'red'. They STACK BENEATH the firm-capacity cap:
 * an infeasible-ELCC red ceiling binds first, and the amber constraint cap is only
 * surfaced when it is the BINDING ceiling (i.e. there was no stricter ELCC red cap).
 * Signal-gated: an intent with none of these declared signals yields NO constraints
 * → no cap (byte-stable, the common case).
 */

/**
 * Derive the declared binding constraints (non-firm-capacity) from an intent.
 * Pure + signal-gated — returns [] when no hard constraint is declared. Mirrors
 * the deterministic risk-seed gates in the type-specific scorers so the headline
 * cap and the category penalties agree on what counts as binding.
 */
export function summarizeIntentConstraints(
  intent: ProjectIntent | undefined | null,
): BindingConstraint[] {
  if (!intent) return [];
  const out: BindingConstraint[] = [];

  // WATER rights — an unpermitted surface / groundwater source is real
  // drought-curtailment exposure for cooling withdrawal. (Mirrors scoreSiteWater.)
  const ws = intent.waterSource;
  if (hasDroughtExposedSource(ws) && ws?.rightsStatus !== 'permitted') {
    out.push({
      kind: 'water-rights',
      // ONLY the exposed sources (see formatDroughtExposedSources).
      note: `Unpermitted ${formatDroughtExposedSources(ws)} water source – drought-curtailment exposure for cooling withdrawal.`,
    });
  }

  // FUEL supply — no declared gas supply, or a required pipeline lateral, is a
  // long-lead single-path COD dependency for a gas build. (Mirrors scoreSiteSupplyChain.)
  const fa = intentHasExplicitGasFreePowerMix(intent) ? undefined : intent.fuelAccess;
  if (fa?.supply === 'none') {
    out.push({
      kind: 'fuel-supply',
      note: 'No declared gas supply – long-lead, single-path fuel-delivery COD dependency.',
    });
  } else if (fa?.supply === 'lateral-required') {
    out.push({
      kind: 'fuel-supply',
      note: 'Pipeline lateral required – long-lead, single-path fuel dependency concentrates COD-schedule risk.',
    });
  }

  // INTERCONNECTION queue — an early-queue grid-import-dependent request carries
  // multi-year energization-date risk. (Mirrors scoreSitePower.) `executed-LGIA`
  // / `none` / absent → not binding.
  const q = intent.interconnectionStatus?.queueStatus;
  if (q === 'pre-application') {
    out.push({
      kind: 'interconnection-queue',
      note: 'Interconnection request is pre-application – multi-year energization-date risk.',
    });
  } else if (q === 'queued' || q === 'study') {
    out.push({
      kind: 'interconnection-queue',
      note: `Interconnection request is ${q} – energization-date risk from study / upgrade timelines.`,
    });
  }

  return out;
}

/** Compose the one-line audit note for one or more binding constraints. */
function constraintCapNote(constraints: BindingConstraint[]): string {
  return constraints.length === 1
    ? `Verdict capped at Conditional – ${constraints[0].note} Resolve to lift the cap.`
    : `Verdict capped at Conditional by ${constraints.length} binding constraints – ${constraints
        .map(c => c.note)
        .join(' ')} Resolve to lift the cap.`;
}

/**
 * Cap a display tier against the worst per-phase firm-capacity verdict AND the
 * multi-axis binding development constraints (HR-4: water rights / fuel supply /
 * interconnection queue) declared on the intent.
 *
 * The firm-capacity (ELCC) cap binds FIRST (it is the only cap that can force
 * 'red'); the binding-constraint cap (ceiling 'amber') STACKS BENEATH it and is
 * surfaced only when it is the binding ceiling. Both only ever LOWER the tier.
 *
 * @param tier        Tier after score + lean reconciliation.
 * @param intent      Project intent (gen-time snapshot) carrying the phases +
 *                    the declared water / fuel / interconnection signals.
 * @returns The (possibly lowered) tier plus, when a cap fired, the audit notes:
 *          `cap` for the firm-capacity (ELCC) cap and `constraintCap` for the
 *          HR-4 binding-constraint cap. A `'none'` tier (no scorer — test-fit /
 *          sentiment) is preserved: the caps only lower a real banded tier, they
 *          never fabricate one, but both surface their audit note so the
 *          exec-summary headline reconciliation can still downgrade.
 *
 * SIGNATURE NOTE: the return adds an OPTIONAL `constraintCap` field — existing
 * callers that destructure `{ tier }` / `{ cap }` are unaffected (byte-stable).
 */
export function capTierByFirmCapacity(
  tier: VerdictTier,
  intent: ProjectIntent | undefined | null,
): {
  tier: VerdictTier;
  cap?: ReportVerdict['firmCapacityCap'];
  constraintCap?: ReportVerdict['constraintCap'];
} {
  const worst = summarizeIntentFeasibility(intent).worstVerdict;
  // HR-4 — declared binding constraints (water / fuel / interconnection).
  const constraints = summarizeIntentConstraints(intent);
  const hasConstraints = constraints.length > 0;

  // P3-3 — no banded tier to cap (executive-summary / sentiment, tier 'none').
  // We do NOT fabricate a band, but if a firm-capacity shortfall OR a binding
  // constraint exists we still SURFACE the cap(s) so the exec-summary
  // `overallVerdict` reconciliation can downgrade the headline GO/NO-GO (the gap
  // the audit flagged: the exec-summary headline was uncapped because it
  // early-returned on 'none'). null / 'feasible' + no constraints → no cap (the
  // common case → byte-stable, no change).
  if (tier === 'none') {
    const out: {
      tier: VerdictTier;
      cap?: ReportVerdict['firmCapacityCap'];
      constraintCap?: ReportVerdict['constraintCap'];
    } = { tier };
    if (worst !== null && worst !== 'feasible') {
      const note =
        worst === 'infeasible'
          ? 'Firm-capacity (ELCC) shortfall – the declared mix cannot serve the 24/7 load; the headline cannot read GO.'
          : 'Firm-capacity (ELCC) shortfall – the declared mix is short of the planning reserve margin; the headline cannot read an unconditional GO.';
      out.cap = { worstVerdict: worst, preCapTier: tier, note, forNoScorerType: true };
    }
    if (hasConstraints) {
      out.constraintCap = {
        constraints,
        preCapTier: tier,
        note: constraintCapNote(constraints),
        forNoScorerType: true,
      };
    }
    return out;
  }

  const tierIdx = CAP_TIER_ORDER.indexOf(tier);

  // ── STEP 1 — firm-capacity (ELCC) cap. Binds FIRST; only cap that forces red.
  // Null (OR-mode / single source / unconfigured — the COMMON case) or a
  // 'feasible' worst-verdict apply NO firm-capacity cap.
  let cappedTier = tier;
  let firmCap: ReportVerdict['firmCapacityCap'];
  if (worst === 'infeasible' || worst === 'marginal') {
    const ceiling: VerdictTier = worst === 'infeasible' ? 'red' : 'amber';
    const ceilingIdx = CAP_TIER_ORDER.indexOf(ceiling);
    // Cap only LOWERS the tier; if the tier is already at/below the ceiling
    // there is nothing to do (and no audit note — the cap was not binding).
    if (ceilingIdx < tierIdx) {
      const note =
        worst === 'infeasible'
          ? 'Verdict capped at the lowest tier – the declared firm-capacity (ELCC) mix cannot serve the 24/7 load; add firm capacity or storage to lift the cap.'
          : 'Verdict capped at Conditional – the declared firm-capacity (ELCC) mix covers the load but is short of the planning reserve margin; add firm headroom to lift the cap.';
      cappedTier = ceiling;
      firmCap = { worstVerdict: worst, preCapTier: tier, note };
    }
  }

  // ── STEP 2 — HR-4 binding-constraint cap. STACKS BENEATH the firm-capacity
  // cap (ceiling 'amber'). It is surfaced ONLY when it is the binding ceiling:
  // i.e. it lowers the (already firm-capacity-capped) tier further. If the
  // firm-capacity cap already pulled the tier to 'amber'/'red', the amber
  // constraint ceiling is not the binding one → no constraintCap (no double
  // audit note for the same displayed tier).
  let constraintCap: ReportVerdict['constraintCap'];
  if (hasConstraints) {
    const ceilingIdx = CAP_TIER_ORDER.indexOf('amber');
    const cappedIdx = CAP_TIER_ORDER.indexOf(cappedTier);
    if (ceilingIdx < cappedIdx) {
      constraintCap = {
        constraints,
        // preCapTier reflects the tier this cap actually lowered FROM (post
        // firm-capacity cap) so the "why?" UI shows the binding step.
        preCapTier: cappedTier,
        note: constraintCapNote(constraints),
      };
      cappedTier = 'amber';
    }
  }

  return {
    tier: cappedTier,
    ...(firmCap ? { cap: firmCap } : {}),
    ...(constraintCap ? { constraintCap } : {}),
  };
}

/**
 * Build the user-facing verdict for a report. The returned `tier` is the
 * post-reconciliation tier (deterministic + LLM lean), then firm-capacity
 * capped (R-FIN-VERDICT-ELCC); `internalScore` is the underlying 0-100
 * number (NEVER displayed except in admin disclosure).
 *
 * Firm-capacity binding: the cap reads the report's gen-time project-intent
 * snapshot (`projectIntentAtGen`, the SC2 reproducibility-correct source —
 * NOT the live site doc). Callers that hold a fresher intent (e.g. a live
 * preview before gen) may pass it explicitly via `intentOverride`.
 */
export interface FrozenFeasibilityDecisionInput {
  reportRevision?: unknown;
  reportType?: string;
  decisionAnalysis?: unknown;
  conclusion?: { recommendation?: string };
  overallStatus?: unknown;
}

/** Saved feasibility decisions are immutable evidence, not a new score input.
 * A recorded hold/no-go can constrain a presentation, never promote it above
 * an existing physical/evidence cap. Generation-time reports have no revision
 * and retain their existing scoring path. */
export function frozenFeasibilityDecisionTier(report: FrozenFeasibilityDecisionInput): VerdictTier | undefined {
  if (!report.reportRevision || (report.reportType ?? 'feasibility') !== 'feasibility') return undefined;
  const decision = report.decisionAnalysis as { semantic?: { systemDecision?: { band?: unknown } } } | undefined;
  const recorded: VerdictTier[] = [];
  if (decision != null) {
    switch (decision.semantic?.systemDecision?.band) {
      case 'go': recorded.push('green'); break;
      case 'conditional': recorded.push('amber'); break;
      case 'no-go': recorded.push('red'); break;
      case 'withheld': return 'none';
      // A malformed saved decision cannot fall through to an upbeat score.
      default: return 'none';
    }
  }
  const recommendation = report.conclusion?.recommendation;
  const canonical = recommendation === 'Decision Pending' ? 'inconclusive' : canonicalFromVerdictWord(recommendation);
  if (canonical === 'proceed') recorded.push('green');
  if (canonical === 'caution') recorded.push('amber');
  if (canonical === 'stop') recorded.push('red');
  if (canonical === 'inconclusive') return 'none';
  const status = String(report.overallStatus ?? '').toUpperCase();
  if (status === 'WITHHELD') return 'none';
  if (status === 'NO_GO' || status === 'RED') recorded.push('red');
  else if (status === 'CONDITIONAL' || status === 'AMBER') recorded.push('amber');
  else if (status === 'GO' || status === 'GREEN') recorded.push('green');
  // The frozen source can precede semantic attachment to the final body.
  // A later score-derived semantic band must not erase its recorded no-go.
  if (recorded.includes('red')) return 'red';
  if (recorded.includes('amber')) return 'amber';
  if (recorded.includes('green')) return 'green';
  switch (String(report.overallStatus ?? '').toUpperCase()) {
    case 'GO': case 'GREEN': return 'green';
    case 'CONDITIONAL': case 'AMBER': return 'amber';
    case 'NO_GO': case 'RED': return 'red';
    default: return undefined;
  }
}

/** Disclose disagreement between saved carriers; reconciliation is conservative
 * presentation only, not a new engineering decision or a rewrite of evidence. */
export function frozenFeasibilityDecisionNotice(report: FrozenFeasibilityDecisionInput): string | undefined {
  if (!report.reportRevision || (report.reportType ?? 'feasibility') !== 'feasibility') return undefined;
  const band = (report.decisionAnalysis as { semantic?: { systemDecision?: { band?: unknown } } } | undefined)?.semantic?.systemDecision?.band;
  const semantic = band === 'go' ? 'proceed' : band === 'conditional' ? 'caution'
    : band === 'no-go' ? 'stop' : band === 'withheld' ? 'inconclusive' : undefined;
  const recommendation = report.conclusion?.recommendation;
  const conclusion = recommendation === 'Decision Pending' ? 'inconclusive' : canonicalFromVerdictWord(recommendation);
  const status = String(report.overallStatus ?? '').toUpperCase();
  const statusVerdict = status === 'WITHHELD' ? 'inconclusive' : status === 'RED' || status === 'NO_GO' ? 'stop' : status === 'AMBER' || status === 'CONDITIONAL' ? 'caution' : status === 'GREEN' || status === 'GO' ? 'proceed' : undefined;
  const severity: Record<CanonicalVerdict, number> = {
    proceed: 0,
    caution: 1,
    stop: 2,
    inconclusive: 3,
    withheld: 3,
  };
  const semanticVsConclusionConflict =
    semantic !== undefined && conclusion !== undefined && semantic !== conclusion;
  const decisionVerdict = conclusion ?? semantic;
  const statusMoreRestrictiveThanDecision =
    statusVerdict !== undefined &&
    decisionVerdict !== undefined &&
    severity[statusVerdict] > severity[decisionVerdict];
  return semanticVsConclusionConflict || statusMoreRestrictiveThanDecision
    ? 'Saved decision fields disagree. The more restrictive recorded recommendation is retained for screening; reconcile the decision before relying on it. Modeled adequacy is not development clearance.'
    : undefined;
}

export function getDisplayVerdict(
  report: SiteRagReport,
  intentOverride?: ProjectIntent | null,
): ReportVerdict {
  const reportType = (report.reportType ?? 'feasibility') as ReportTypeId;
  const reportTypeShortLabel =
    REPORT_TYPE_REGISTRY[reportType]?.shortLabel ?? 'Report';

  const internalScore = resolveInternalScore(report);
  // Q12 / D6 — a FINANCIAL report's deterministic tier is the ENGINE BAND
  // itself (`financialStampTier`), not `overallScore` re-banded. Every other
  // type (and a legacy financial report with no stamp) bands the score exactly
  // as before.
  const stampTier = financialStampTier(report);
  const deterministicTier = stampTier ?? tierFromScore(internalScore, reportType);

  // Capture verdictLean from the report (persisted by siteRagService from
  // the LLM narrative response). Optional — legacy reports lack it.
  //
  // Q12 — types whose verdict is a MODEL OUTPUT are EXEMPT: see
  // `VERDICT_LEAN_EXEMPT_REPORT_TYPES` for why a narrative nudge over an
  // engine-computed band is noise, not evidence. Dropping `rawLean` here (as
  // opposed to clamping it later) also keeps `source` reading 'deterministic'
  // and leaves `lean` off the returned verdict, so no downstream "Why this
  // tier?" surface can advertise a lean that was never applied.
  const hasFrozenSystemDecision = Boolean(
    report.reportRevision
    && (report.decisionAnalysis as { semantic?: { systemDecision?: { band?: unknown } } } | undefined)?.semantic?.systemDecision?.band,
  );
  const rawLean = reportTypeAllowsVerdictLean(reportType) && !hasFrozenSystemDecision
    ? (report as { verdictLean?: VerdictLean }).verdictLean
    : undefined;
  const leanedTier = reconcileVerdict(deterministicTier, rawLean);

  // R-FIN-VERDICT-ELCC — bind the headline to firm-capacity reality. Reads
  // the gen-time intent snapshot unless the caller threads a fresher one.
  const intent = deriveIntentForPlanningReport(report,
    intentOverride !== undefined ? intentOverride : report.projectIntentAtGen);
  const {
    tier: firmCappedTier,
    cap: firmCapacityCap,
    constraintCap,
  } = capTierByFirmCapacity(leanedTier, intent);

  // EA8 — evidence-tier cap. A GREEN/Strong tier on evidence that is mostly
  // regional baselines / LLM synthesis (tier1 share < STRONG_VERDICT_TIER1_FLOOR)
  // is not defensible. We LOWER the displayed tier to 'amber' so every surface
  // (verdict dot, journal cover, data-rag tone, portfolio band, conclusion
  // caption) inherits the cap consistently — previously only the
  // ConclusionCallout caption mentioned it while the verdict stayed green.
  //
  // Signal-gated to preserve the no-override invariant: the cap fires ONLY when
  // `sectionProvenance` is recorded (a legacy report with none is byte-stable)
  // and the tier is the GREEN it constrains; it STACKS on the firm-capacity cap
  // (each can only push the verdict toward caution, never up).
  let finalTier = firmCappedTier;
  let evidenceTierCap: ReportVerdict['evidenceTierCap'];
  if (
    finalTier === 'green' &&
    Object.keys(report.sectionProvenance ?? {}).length > 0 &&
    !tierAllowsStrongVerdict(report)
  ) {
    const share = computeTierShare(report);
    const upliftDocCount = countCriticalUploadsToLift(report);
    evidenceTierCap = {
      preCapTier: finalTier,
      tier1Share: share.tier1,
      upliftDocCount,
      note:
        upliftDocCount > 0
          ? `Verdict capped at Conditional by evidence tier – current evidence is mostly regional baselines; upload ${upliftDocCount} more critical doc${upliftDocCount === 1 ? '' : 's'} to substantiate a Strong rating.`
          : 'Verdict capped at Conditional by evidence tier – current evidence is mostly regional baselines; site-specific documents would substantiate a Strong rating.',
    };
    finalTier = 'amber';
  }

  // The absorbed screen retains a hard stop for an affirmative permit denial.
  // Apply through this shared verdict so portfolio floors and the decision
  // rollup cannot disagree. Unverified questions and negated denials never cap.
  let waterScreenCap: ReportVerdict['waterScreenCap'];
  if (reportType === 'feasibility' && finalTier !== 'none' && finalTier !== 'red'
    && waterScreenHasDeniedPermit(report)) {
    waterScreenCap = {
      preCapTier: finalTier,
      note: 'Water screen: withdrawal permit denied — no lawful withdrawal path.',
    };
    finalTier = 'red';
  }

  // Frozen full-report readers must not upgrade the decision shown by the
  // same revision's Summary. Preserve any stricter existing physical/evidence
  // hold, and never use a recorded positive decision to raise the computed tier.
  const savedTier = frozenFeasibilityDecisionTier(report);
  const severity: Record<VerdictTier, number> = { green: 0, amber: 1, red: 2, none: 3 };
  const savedDecisionCap = savedTier !== undefined && severity[savedTier] > severity[finalTier]
    ? { preCapTier: finalTier, recordedTier: savedTier }
    : undefined;
  if (savedDecisionCap) finalTier = savedDecisionCap.recordedTier;

  // `source` reflects whether the LLM lean moved the tier off the
  // deterministic band. A firm-capacity cap is an independent overlay and
  // does NOT change the source attribution (it is surfaced via
  // `firmCapacityCap`, mirroring the EA8 evidence-tier cap's separation).
  const leaned = leanedTier !== deterministicTier;
  const source: VerdictSource =
    deterministicTier === 'none' ? 'none'
    : leaned ? 'llm-leaned'
    : 'deterministic';

  // Only surface the lean when it was actually applied; the consumer uses
  // this to render the "Why {tier}?" expandable.
  const appliedLean = leaned && rawLean
    ? {
        delta: rawLean.tierAdjustment,
        drivers: (rawLean.drivers ?? [])
          .map(d => d?.trim?.() ?? '')
          .filter(d => d.length >= 8),
      }
    : undefined;

  // Q12 item 3 — a WITHHELD deterministic verdict (financial stamp with
  // `band: null`) must read as WITHHELD everywhere, not as the generic tier-
  // 'none' em-dash of some other type. The stamp's `verdictWithheldReason`
  // names which kind; `financialWithheldNote` keeps "cannot compute" and
  // "computed but incoherent" distinguishable, because they have different
  // fixes.
  const withheldReason = report.financialAnalysis?.verdictWithheldReason;
  const withheldVerdict: ReportVerdict['withheldVerdict'] | undefined =
    stampTier === 'none'
      ? {
          ...(withheldReason ? { reason: withheldReason } : {}),
          label: VERDICT_WITHHELD_LABEL,
          note: financialWithheldNote(withheldReason),
        }
      : undefined;

  return {
    tier: finalTier,
    ...(savedTier === 'none' ? { savedDecisionPending: true } : {}),
    ...(frozenFeasibilityDecisionNotice(report) ? { savedDecisionConflict: frozenFeasibilityDecisionNotice(report) } : {}),
    ...(savedDecisionCap ? { savedDecisionCap } : {}),
    label: savedTier === 'none' ? 'Decision pending' : withheldVerdict && finalTier === 'none'
      ? withheldVerdict.label
      : tierLabel(finalTier, reportType, 'overall'),
    reportTypeShortLabel,
    source,
    ...(internalScore != null ? { internalScore: Math.round(internalScore) } : {}),
    deterministicTier,
    ...(appliedLean && !savedDecisionCap ? { lean: appliedLean } : {}),
    ...(firmCapacityCap ? { firmCapacityCap } : {}),
    ...(constraintCap ? { constraintCap } : {}),
    ...(evidenceTierCap ? { evidenceTierCap } : {}),
    ...(withheldVerdict ? { withheldVerdict } : {}),
    ...(waterScreenCap ? { waterScreenCap } : {}),
  };
}
/* ── Q12 / D6 — ONE financial verdict, with the gap explained ────────────────
 *
 * Before this, a financial report published TWO verdicts: `DecisionBandSection`
 * rendered the RAW engine band while the cover, dashboard, Go/No-Go rollup and
 * the DOCX tint rendered `getDisplayVerdict`, which stacks caps on top. A
 * "Viable · 82/100" body could therefore sit under a "Conditional" cover with
 * no explanation of the difference anywhere on the page.
 *
 * DECISION (Q12): keep the caps, disclose them.
 *
 *   - The LLM `verdictLean` is GONE for financial (see
 *     `VERDICT_LEAN_EXEMPT_REPORT_TYPES`) — a narrative must not move a number.
 *   - The evidence-tier (EA8), firm-capacity (ELCC) and binding-constraint caps
 *     STAY. They are monotonic-DOWN and each encodes a real epistemic limit: a
 *     model is only as good as the inputs it was fed, and "the arithmetic says
 *     Viable on regional-baseline CAPEX with no vendor quote" is a materially
 *     weaker claim than "Viable on a priced BoM". Suppressing them would let a
 *     tier-4 synthesis publish an unqualified Viable.
 *   - But a cap is only defensible if the reader can SEE it. So every surface
 *     resolves through this helper, which carries BOTH the displayed tier and
 *     the model's own band, plus the note that names each binding cap. The
 *     reader never sees two verdicts without an explanation of the gap.
 */

/** Which cap moved a financial report's displayed tier off its model band. */
export type FinancialVerdictCapKind =
  | 'firm-capacity'
  | 'binding-constraint'
  | 'evidence-tier'
  | 'narrative-lean';

/** One binding cap, named for display. */
export interface FinancialVerdictCap {
  kind: FinancialVerdictCapKind;
  /** Short name of the cap ("Evidence-tier cap"). */
  name: string;
  /** The cap's own audit note (verbatim from the verdict). */
  note: string;
}

const FINANCIAL_CAP_NAMES: Record<FinancialVerdictCapKind, string> = {
  'firm-capacity': 'Firm-capacity (ELCC) cap',
  'binding-constraint': 'Binding-constraint cap',
  'evidence-tier': 'Evidence-tier cap',
  'narrative-lean': 'Narrative lean',
};

/** The ONE financial verdict every surface renders, plus its provenance. */
export interface FinancialDisplayVerdict {
  /** The full display verdict (caps, score, lean) for consumers that need it. */
  verdict: ReportVerdict;
  /** The tier EVERY surface displays. Body, cover, rollup and DOCX agree. */
  tier: VerdictTier;
  /** Display label for `tier` ("Viable" / "Conditional" / "Unfinanceable" /
   *  "Verdict withheld"). */
  label: string;
  /** The engine stamp's own band — what the MODEL computed, uncapped. */
  modelBand: 'green' | 'amber' | 'red' | null;
  /** Display label for `modelBand`. */
  modelLabel: string;
  /** False when the report carries no `financialAnalysis` stamp at all. */
  hasStamp: boolean;
  /** True when the model declined to band (stamp present, `band: null`). */
  withheld: boolean;
  /** Which kind of withholding, when the stamp recorded it. */
  withheldReason?: FinancialWithheldReason;
  /** True when a cap moved the displayed tier off the model band. */
  capped: boolean;
  /** The caps that bound, in the order they were applied. */
  caps: FinancialVerdictCap[];
  /** One paragraph naming the gap and every cap behind it ('' when uncapped). */
  capNote: string;
}

/** Band → the tier it displays as. `null` (withheld) is tier 'none'. */
function financialBandTier(band: 'green' | 'amber' | 'red' | null | undefined): VerdictTier {
  if (band === 'green' || band === 'amber' || band === 'red') return band;
  return 'none';
}

/**
 * Resolve the ONE financial verdict for a report, with the cap disclosure.
 *
 * Pure. Safe on any report (a non-financial one simply reports its own display
 * verdict with `hasStamp: false` and no caps), so callers do not need to branch
 * on report type before calling.
 */
export function resolveFinancialDisplayVerdict(
  report: SiteRagReport,
  intentOverride?: ProjectIntent | null,
): FinancialDisplayVerdict {
  const verdict = getDisplayVerdict(report, intentOverride);
  const stamp = report.financialAnalysis;
  const hasStamp = Boolean(stamp);
  const modelBand = stamp ? stamp.band : null;
  const modelTier = financialBandTier(modelBand);
  const withheld = hasStamp && modelBand === null;

  const modelLabel = withheld || !hasStamp
    ? VERDICT_WITHHELD_LABEL
    : tierLabel(modelTier, 'financial', 'overall');

  const caps: FinancialVerdictCap[] = [];
  // Order = the order `getDisplayVerdict` applies them, so the note reads as
  // the chain that actually ran.
  if (verdict.lean) {
    // Defensive: financial is lean-EXEMPT, so this should be unreachable for a
    // financial report. If a future policy edit re-admits the lean, the reader
    // sees it named rather than silently absorbed into the tier.
    caps.push({
      kind: 'narrative-lean',
      name: FINANCIAL_CAP_NAMES['narrative-lean'],
      note: `Narrative adjusted the deterministic tier by ${verdict.lean.delta > 0 ? '+' : ''}${verdict.lean.delta}: ${verdict.lean.drivers.join('; ')}`,
    });
  }
  if (verdict.firmCapacityCap && !verdict.firmCapacityCap.forNoScorerType) {
    caps.push({
      kind: 'firm-capacity',
      name: FINANCIAL_CAP_NAMES['firm-capacity'],
      note: verdict.firmCapacityCap.note,
    });
  }
  if (verdict.constraintCap && !verdict.constraintCap.forNoScorerType) {
    caps.push({
      kind: 'binding-constraint',
      name: FINANCIAL_CAP_NAMES['binding-constraint'],
      note: verdict.constraintCap.note,
    });
  }
  if (verdict.evidenceTierCap) {
    caps.push({
      kind: 'evidence-tier',
      name: FINANCIAL_CAP_NAMES['evidence-tier'],
      note: verdict.evidenceTierCap.note,
    });
  }

  // A gap exists whenever the displayed tier is not the model's own band. Caps
  // are monotonic-down, so the gap is always "displayed is more cautious".
  const capped = hasStamp && verdict.tier !== modelTier;

  const capNote = capped
    ? [
        `Displayed verdict ${verdict.label} — the deterministic model bands this ${modelLabel}.`,
        ...(caps.length > 0
          ? caps.map((c) => `${c.name}: ${c.note}`)
          : ['The displayed tier was capped by an evidence or feasibility limit recorded on the report.']),
      ].join(' ')
    : '';

  return {
    verdict,
    tier: verdict.tier,
    label: verdict.label,
    modelBand,
    modelLabel,
    hasStamp,
    withheld,
    ...(stamp?.verdictWithheldReason ? { withheldReason: stamp.verdictWithheldReason } : {}),
    capped,
    caps,
    capNote,
  };
}

/* ── PG-3 / PG-10 — Executive-summary cover verdict SSOT ─────────────────────
 *
 * The Executive Summary is the ONE report whose entire purpose is the GO/NO-GO
 * call, yet `executive-summary ∈ NO_SCORING_REPORT_TYPES`, so `getDisplayVerdict`
 * returns `tier: 'none'` for it (correct for a generic no-scorer cover badge, but
 * wrong for the exec-summary cover). Every cover surface gates the verdict on
 * `tier !== 'none'`, so the exec-summary cover carried NO verdict on view / PDF /
 * DOCX / PPTX — the verdict reached the reader ONLY through the body Decision
 * Guide section (which the office exports also drop). PG-3.
 *
 * The authoritative exec-summary verdict is `buildExecutiveDecisionRollup`'s full
 * cap stack (adequacy → withheld, worst-source floor, firm-capacity, sim-
 * feasibility, completeness → conditional), snapshotted at gen-time onto
 * `report.executiveDecisionRollupAtGen` by `runTypedReportGeneration`. That
 * snapshot is the SSOT for the exec headline (PG-10) — the body Decision Guide
 * section already PREFERS it for view↔PDF byte-identity. `resolveCoverVerdict`
 * makes the four COVERS source the exec headline from the SAME snapshot, so the
 * cover, the body band, and the office exports speak one verdict.
 *
 * The rollup vocabulary (`DerivedBand`: go / conditional-high / conditional /
 * no-go / null) is normalized into the `VerdictTier` the covers already render
 * (green / amber / red / none) so the dot/stamp colour stays consistent across
 * every surface; the rollup's own `label` (e.g. "GO" / "CONDITIONAL" / "NO-GO" /
 * "Insufficient evidence") is surfaced verbatim so the cover badge text matches
 * the body Decision Guide band exactly. A withheld rollup (`band === null`) maps
 * to `tier: 'none'` so the cover honestly collapses the verdict rather than
 * asserting a banded call on inadequate evidence (the no-assert-on-no-evidence
 * North Star).
 *
 * For EVERY OTHER report type `resolveCoverVerdict` delegates to
 * `getDisplayVerdict` verbatim — so the change is scoped to the exec-summary
 * cover and is byte-stable for all other types.
 */

/** Rollup snapshot type (read off the report; type-only — no runtime cycle). */
type DecisionGuideRollupSnapshot = NonNullable<
  SiteRagReport['executiveDecisionRollupAtGen']
>;

/**
 * Normalize the exec-summary decision-rollup `band` into the cover `VerdictTier`.
 * The single mapping every surface shares so the GO/NO-GO dot colour cannot drift:
 *   - 'go'                              → 'green'
 *   - 'conditional-high' | 'conditional' → 'amber'
 *   - 'no-go'                           → 'red'
 *   - null (withheld / insufficient)     → 'none'  (cover collapses honestly)
 */
export function execRollupBandToTier(
  band: DecisionGuideRollupSnapshot['band'],
): VerdictTier {
  switch (band) {
    case 'go':
      return 'green';
    case 'conditional-high':
    case 'conditional':
      return 'amber';
    case 'no-go':
      return 'red';
    default:
      // null / unknown → withheld; the cover must not assert a band.
      return 'none';
  }
}

/**
 * Resolve the verdict to render on a report COVER (view / PDF / DOCX / PPTX).
 *
 * For the EXECUTIVE SUMMARY this is the gen-time decision-rollup snapshot
 * (`report.executiveDecisionRollupAtGen`) — the full-cap-stack SSOT (PG-10) —
 * normalized into the `ReportVerdict` shape the covers already consume, so the
 * exec cover finally carries a verdict (PG-3) that matches the body Decision
 * Guide band byte-for-byte. The rollup's own `label` is surfaced verbatim
 * ("GO" / "CONDITIONAL" / "NO-GO" / "Insufficient evidence") rather than the
 * generic OVERALL_LABELS, so the cover and the body band speak ONE calibrated
 * vocabulary. A legacy exec-summary report stamped before the snapshot field
 * existed (no `executiveDecisionRollupAtGen`) falls back to `getDisplayVerdict`
 * verbatim — byte-stable, no fabricated band.
 *
 * For EVERY OTHER report type this delegates to `getDisplayVerdict` unchanged.
 *
 * Returns the same `ReportVerdict` shape as `getDisplayVerdict`, so consumers can
 * swap `getDisplayVerdict(report)` → `resolveCoverVerdict(report)` on the cover
 * path with no other change (the `tier !== 'none'` gate, `label`,
 * `reportTypeShortLabel`, and tier→role-colour maps all behave identically).
 */
export function resolveCoverVerdict(
  report: SiteRagReport,
  intentOverride?: ProjectIntent | null,
): ReportVerdict {
  const reportType = (report.reportType ?? 'feasibility') as ReportTypeId;

  // Non-exec types: the cover verdict IS the display verdict (byte-stable).
  if (reportType !== 'executive-summary') {
    return getDisplayVerdict(report, intentOverride);
  }

  // Exec-summary: prefer the gen-time rollup snapshot (the SSOT headline). A
  // legacy report without it falls back to getDisplayVerdict (tier 'none').
  const rollup = report.executiveDecisionRollupAtGen;
  if (!rollup) {
    return getDisplayVerdict(report, intentOverride);
  }

  const reportTypeShortLabel =
    REPORT_TYPE_REGISTRY[reportType]?.shortLabel ?? 'Report';
  const tier = execRollupBandToTier(rollup.band);
  // The rollup label is the canonical headline word; fall back to the
  // exec-summary OVERALL_LABELS for the tier only if the snapshot somehow
  // carries an empty label (defensive — the rollup always sets one).
  const label =
    typeof rollup.label === 'string' && rollup.label.trim().length > 0
      ? rollup.label
      : tierLabel(tier, reportType, 'overall');

  return {
    tier,
    label,
    reportTypeShortLabel,
    // The rollup is a deterministic, LLM-free engine; attribute accordingly.
    source: tier === 'none' ? 'none' : 'deterministic',
  };
}

/* ── PG-10 — ONE calibrated verdict vocabulary ───────────────────────────────
 *
 * Three verdict vocabularies coexist across the platform and risked drifting:
 *   - `DerivedBand`  go / conditional-high / conditional / no-go / null  (rollup)
 *   - `VerdictTier`  green / amber / red / none                          (covers)
 *   - PROCEED / CAUTION / INCONCLUSIVE / STOP                            (D3 check)
 *
 * `CanonicalVerdict` is the SSOT calibration — one ordered set every surface maps
 * onto, so a cross-surface verdict-identity gate (P0-3) and the D3 consistency
 * check can compare like-for-like instead of guessing at synonyms. The ordering
 * runs best→worst (proceed → withheld) so `compareCanonicalVerdict` and a
 * "stronger than the weakest source" check have a single monotonic scale.
 *
 * `withheld` is BELOW `stop` only on the no-assert axis — a withheld call is the
 * absence of a defensible verdict, distinct from a confident STOP. Consumers that
 * only care about "is the headline at least as cautious as its sources" treat
 * `withheld` as the most cautious (it asserts nothing positive).
 */
export type CanonicalVerdict =
  | 'proceed'
  | 'caution'
  | 'inconclusive'
  | 'stop'
  | 'withheld';

/** Best→most-cautious ordering. Lower index = stronger (closer to GO). */
const CANONICAL_VERDICT_ORDER: readonly CanonicalVerdict[] = [
  'proceed',
  'caution',
  'inconclusive',
  'stop',
  'withheld',
];

/** Map a rollup `DerivedBand` onto the canonical vocabulary. */
export function canonicalFromBand(
  band: DecisionGuideRollupSnapshot['band'],
): CanonicalVerdict {
  switch (band) {
    case 'go':
      return 'proceed';
    case 'conditional-high':
    case 'conditional':
      return 'caution';
    case 'no-go':
      return 'stop';
    default:
      return 'withheld';
  }
}

/** Map a cover `VerdictTier` onto the canonical vocabulary. */
export function canonicalFromTier(tier: VerdictTier): CanonicalVerdict {
  switch (tier) {
    case 'green':
      return 'proceed';
    case 'amber':
      return 'caution';
    case 'red':
      return 'stop';
    default:
      return 'withheld';
  }
}

/**
 * Map a free-text verdict word (the legacy PROCEED / CAUTION / INCONCLUSIVE /
 * STOP vocabulary, or a banded synonym persisted on older reports) onto the
 * canonical vocabulary. Returns `null` when the token is unrecognized so callers
 * can skip it (no-assert) rather than mis-bucket it.
 */
export function canonicalFromVerdictWord(
  word: string | null | undefined,
): CanonicalVerdict | null {
  if (typeof word !== 'string') return null;
  const w = word.trim().toUpperCase();
  if (w.length === 0) return null;
  switch (w) {
    case 'PROCEED':
    // Historical labels stay parseable FOREVER: persisted reports and decision
    // records carry the words that were current when they were written, and a
    // rename must never make old evidence unreadable.
    case 'GO':
    case 'VIABLE':
    case 'STRONG':
      return 'proceed';
    case 'CAUTION':
    case 'CONDITIONAL':
    case 'CONDITIONAL HIGH':
    case 'CONDITIONAL-HIGH':
    case 'PROCEED WITH CONDITIONS':
    case 'PROCEED WITH ENHANCED DILIGENCE':
      return 'caution';
    case 'INCONCLUSIVE':
    case 'INSUFFICIENT EVIDENCE':
    case 'WITHHELD':
      return 'inconclusive';
    case 'STOP':
    case 'DO NOT PROCEED ON CURRENT EVIDENCE':
    case 'DO NOT PROCEED':
    case 'NO-GO':
    case 'NO GO':
    case 'NOGO':
    case 'CHALLENGING':
    case 'UNFINANCEABLE':
      return 'stop';
    default:
      return null;
  }
}

/** Ordinal rank of a canonical verdict (lower = stronger / closer to GO). */
export function canonicalVerdictRank(verdict: CanonicalVerdict): number {
  return CANONICAL_VERDICT_ORDER.indexOf(verdict);
}

/* ── Evidence Assist EA8 — tier-weighted verdict cap ────────────────
 *
 * EA8 adds a second reconciliation layer on top of SF's tier+lean
 * structure: if the evidence backing the report comes mostly from
 * Tier-3 regional baselines or Tier-4 LLM synthesis, a green tier is
 * not credibly defensible regardless of what the deterministic scorer
 * computed. The cap surfaces explicitly in the conclusion callout:
 * "Verdict capped at Conditional due to evidence tier — upload {N}
 * more critical docs for Strong rating."
 *
 * Tier weights:
 *   Tier 1 (site doc):           1.0  full weight
 *   Tier 2 (cross-report reuse): 0.9  near-full weight
 *   Tier 3 (regional baseline):  0.7  partial weight
 *   Tier 4 (LLM synthesis):      0.4  fractional weight
 *
 * The cap is independent of SF's lean — both signals can fire on the
 * same report. Lean determines tier movement (±1); cap determines
 * whether a green tier is allowed at all.
 */

export const TIER_WEIGHTS: Readonly<Record<1 | 2 | 3 | 4, number>> = {
  1: 1.0,
  2: 0.9,
  3: 0.7,
  4: 0.4,
};

/** Strong-verdict requires at least this fraction of weighted sections
 *  to come from Tier-1 evidence. */
export const STRONG_VERDICT_TIER1_FLOOR = 0.5;

export interface VerdictTierShare {
  tier1: number;
  tier2: number;
  tier3: number;
  tier4: number;
}

/**
 * Compute the weighted-tier share for the report.
 * Returns shares summing to 1.0 within the weighted space; empty
 * provenance returns all-zeros (caller treats as neutral).
 */
export function computeTierShare(report: SiteRagReport): VerdictTierShare {
  const provenance = report.sectionProvenance ?? {};
  const entries = Object.values(provenance);
  if (entries.length === 0) {
    return { tier1: 0, tier2: 0, tier3: 0, tier4: 0 };
  }
  const counts: Record<1 | 2 | 3 | 4, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
  for (const e of entries) counts[e.tier]++;
  const weightedTotal =
    counts[1] * TIER_WEIGHTS[1]
    + counts[2] * TIER_WEIGHTS[2]
    + counts[3] * TIER_WEIGHTS[3]
    + counts[4] * TIER_WEIGHTS[4];
  const denom = weightedTotal > 0 ? weightedTotal : 1;
  return {
    tier1: (counts[1] * TIER_WEIGHTS[1]) / denom,
    tier2: (counts[2] * TIER_WEIGHTS[2]) / denom,
    tier3: (counts[3] * TIER_WEIGHTS[3]) / denom,
    tier4: (counts[4] * TIER_WEIGHTS[4]) / denom,
  };
}

/** True when the evidence profile permits a green/strong verdict. */
export function tierAllowsStrongVerdict(report: SiteRagReport): boolean {
  const share = computeTierShare(report);
  return share.tier1 >= STRONG_VERDICT_TIER1_FLOOR;
}

/**
 * Count critical missing docs whose upload would lift the cap.
 * Reads evidenceConfidence.missingCriticalIds when available; falls
 * back to counting Tier 3/4 provenance entries.
 */
export function countCriticalUploadsToLift(report: SiteRagReport): number {
  const missingCritical = report.evidenceConfidence?.missingCriticalIds?.length ?? 0;
  if (missingCritical > 0) return missingCritical;
  const prov = report.sectionProvenance ?? {};
  return Object.values(prov).filter(e => e.tier >= 3).length;
}

/**
 * Render the tier-cap annotation for the conclusion callout.
 *
 * The EA8 cap is now APPLIED inside `getDisplayVerdict` (it lowers the tier to
 * 'amber' and records `verdict.evidenceTierCap`), so the annotation is the
 * EXPLANATION of an effect that already happened, not the only effect. When the
 * cap fired, `verdict.evidenceTierCap.note` is the canonical caption (so the
 * displayed tier and the caption can never disagree). The legacy green-path
 * branch is preserved for callers that pass a verdict computed WITHOUT the cap
 * (e.g. a raw tier from an external source), so the annotation never silently
 * vanishes.
 *
 * Returns '' when no cap applies:
 *   - the cap already fired → emit its recorded note.
 *   - no provenance recorded (legacy report) → no cap
 *   - tier already amber/red/none with no cap → no cap (only matters for green)
 *   - tier1 share ≥ floor → no cap (evidence supports green)
 */
export function getVerdictCapAnnotation(
  report: SiteRagReport,
  verdict: ReportVerdict,
): string {
  // A conflicting saved decision remains a review issue, not silent authority.
  const conflictNotice = frozenFeasibilityDecisionNotice(report);
  if (conflictNotice) return [conflictNotice, verdict.evidenceTierCap?.note].filter(Boolean).join(' ');
  // The cap already fired inside getDisplayVerdict — its note is the SSOT.
  if (verdict.savedDecisionCap) {
    const saved = verdict.savedDecisionCap.recordedTier === 'none'
      ? 'The frozen screening decision is withheld; modeled adequacy does not clear that evidence hold.'
      : 'The frozen screening recommendation remains controlling; a modeled adequacy or score label does not clear its conditions.';
    return [saved, verdict.evidenceTierCap?.note].filter(Boolean).join(' ');
  }
  if (verdict.evidenceTierCap) return verdict.evidenceTierCap.note;
  if (verdict.tier !== 'green') return '';
  const provenance = report.sectionProvenance ?? {};
  if (Object.keys(provenance).length === 0) return '';
  if (tierAllowsStrongVerdict(report)) return '';
  const n = countCriticalUploadsToLift(report);
  if (n > 0) {
    return `Verdict capped – current evidence is mostly regional baselines; upload ${n} more critical doc${n === 1 ? '' : 's'} to substantiate a Strong rating.`;
  }
  return 'Verdict capped – current evidence is mostly regional baselines; site-specific documents would substantiate a Strong rating.';
}

/* ── End EA8 tier-cap block ─────────────────────────────────────────
 *
 * The block below was EA's reportVerdict.ts stub. SF's full file
 * above supersedes it. The merge keeps EA's tier-weighting helpers
 * (above) and discards EA's stub `getDisplayVerdict` / `VerdictDisplay`
 * shape — callers use SF's `ReportVerdict` shape via the existing
 * `getDisplayVerdict` defined earlier in this file.
 */
